import React, { useEffect, useRef, useState } from 'react';
import { Stage, Layer, Image as KonvaImage, Transformer, Rect } from 'react-konva';
import Konva from 'konva';
import {
  Box, Button, IconButton, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, List, ListItemButton, ListItemText, CircularProgress, Typography,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  Home as HomeIcon,
  AddPhotoAlternate as AddPhotoIcon,
  Delete as DeleteIcon,
  RotateLeft as RotateLeftIcon,
  RotateRight as RotateRightIcon,
  RestartAlt as StraightenIcon,
  FlipToFront as FrontIcon,
  FlipToBack as BackIcon,
  Download as DownloadIcon,
  Save as SaveIcon,
  Collections as CollectionsIcon,
  GridView as GridViewIcon,
  ViewList as ViewListIcon,
} from '@mui/icons-material';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useInventoryContext } from '../contexts/InventoryContext';
import { FeatureLockedOverlay } from '../components/subscription/FeatureLockedOverlay';
import { useNavigate, useLocation } from 'react-router-dom';
import { TopBar } from '../layout/TopBar';
import { saveMesa, getUserMesas, getMesa, deleteMesa, saveMesaPreview, Mesa, MesaElement } from '../services/mesaService';
import { getUserEvents, updateEvent, Event } from '../services/eventsService';
import { removeBackground } from '../services/bgRemoval';
import { scrollableSx } from '../utils/scrollableSx';
import { AutoFixHigh as MagicIcon } from '@mui/icons-material';

const tap = async (style: ImpactStyle = ImpactStyle.Light) => {
  try { await Haptics.impact({ style }); } catch { void 0; }
};

const useHtmlImage = (src: string): HTMLImageElement | undefined => {
  const [image, setImage] = useState<HTMLImageElement>();
  useEffect(() => {
    let cancelled = false;
    const load = (useCors: boolean) => {
      const img = new window.Image();
      if (useCors) img.crossOrigin = 'anonymous';
      img.onload = () => { if (!cancelled) setImage(img); };
      img.onerror = () => { if (!cancelled && useCors) load(false); };
      img.src = src;
    };
    load(true);
    return () => { cancelled = true; };
  }, [src]);
  return image;
};

const MesaImage: React.FC<{
  element: MesaElement;
  isSelected: boolean;
  onSelect: () => void;
  onChange: (next: MesaElement) => void;
}> = ({ element, isSelected, onSelect, onChange }) => {
  const image = useHtmlImage(element.src);
  const shapeRef = useRef<Konva.Image>(null);
  const trRef = useRef<Konva.Transformer>(null);

  useEffect(() => {
    if (isSelected && trRef.current && shapeRef.current) {
      trRef.current.nodes([shapeRef.current]);
      trRef.current.getLayer()?.batchDraw();
    }
  }, [isSelected, image]);

  return (
    <>
      <KonvaImage
        ref={shapeRef}
        image={image}
        x={element.x}
        y={element.y}
        width={element.width}
        height={element.height}
        rotation={element.rotation}
        draggable
        onClick={onSelect}
        onTap={onSelect}
        onDragStart={() => tap()}
        onDragEnd={(e) => { tap(); onChange({ ...element, x: e.target.x(), y: e.target.y() }); }}
        onTransformEnd={() => {
          const node = shapeRef.current;
          if (!node) return;
          const scaleX = node.scaleX();
          const scaleY = node.scaleY();
          node.scaleX(1);
          node.scaleY(1);
          onChange({
            ...element,
            x: node.x(),
            y: node.y(),
            width: Math.max(20, node.width() * scaleX),
            height: Math.max(20, node.height() * scaleY),
            rotation: node.rotation(),
          });
        }}
      />
      {isSelected && (
        <Transformer
          ref={trRef}
          rotateEnabled
          keepRatio
          enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right']}
          boundBoxFunc={(oldBox, newBox) => (newBox.width < 20 || newBox.height < 20 ? oldBox : newBox)}
        />
      )}
    </>
  );
};

export const MesaVirtualPage: React.FC = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const { isPro } = useSubscription();
  const { items: acervoItems } = useInventoryContext();
  const location = useLocation();
  const navigate = useNavigate();
  const stageRef = useRef<Konva.Stage>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const idCounter = useRef(0);

  const [elements, setElements] = useState<MesaElement[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  const [mesaId, setMesaId] = useState<string | null>(null);
  const [mesaName, setMesaName] = useState('Minha mesa');
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mesasDialogOpen, setMesasDialogOpen] = useState(false);
  const [savedMesas, setSavedMesas] = useState<Mesa[]>([]);
  const [loadingMesas, setLoadingMesas] = useState(false);

  const persist = async (asNew: boolean) => {
    if (!user?.uid) return;
    setSaving(true);
    setSelectedId(null);
    try {
      await new Promise((r) => setTimeout(r, 50));
      let thumb: string | undefined;
      try { thumb = stageRef.current?.toDataURL({ pixelRatio: 0.4 }); } catch { thumb = undefined; }
      const saved = await saveMesa(user.uid, asNew ? null : mesaId, mesaName, elements, size, thumb);
      setMesaId(saved.id);
      setElements(saved.elements);
      setSaveDialogOpen(false);
      setSnackbar({ open: true, message: asNew && mesaId ? 'Variação salva na sua conta' : 'Mesa salva na sua conta', severity: 'success' });
      tap(ImpactStyle.Heavy);
    } catch (e: any) {
      setSnackbar({ open: true, message: e?.message || 'Erro ao salvar mesa', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const openMesasDialog = async () => {
    setMesasDialogOpen(true);
    if (!user?.uid) return;
    setLoadingMesas(true);
    try {
      setSavedMesas(await getUserMesas(user.uid));
    } catch {
      setSnackbar({ open: true, message: 'Erro ao carregar mesas', severity: 'error' });
    } finally {
      setLoadingMesas(false);
    }
  };

  const openMesa = async (id: string) => {
    if (!user?.uid) return;
    try {
      const mesa = await getMesa(user.uid, id);
      if (!mesa) return;
      const saved = mesa.canvas;
      const factor = saved && saved.width > 0 && saved.height > 0
        ? Math.min(size.width / saved.width, size.height / saved.height)
        : 1;
      setElements(
        factor === 1
          ? mesa.elements
          : mesa.elements.map((el) => ({
              ...el,
              x: el.x * factor,
              y: el.y * factor,
              width: el.width * factor,
              height: el.height * factor,
            }))
      );
      setMesaId(mesa.id);
      setMesaName(mesa.name);
      setSelectedId(null);
      setMesasDialogOpen(false);
    } catch {
      setSnackbar({ open: true, message: 'Erro ao abrir mesa', severity: 'error' });
    }
  };

  const removeMesa = async (mesa: Mesa) => {
    if (!user?.uid) return;
    try {
      await deleteMesa(user.uid, mesa.id);
      setSavedMesas((prev) => prev.filter((m) => m.id !== mesa.id));
      if (mesaId === mesa.id) {
        setMesaId(null);
        setElements([]);
        setSelectedId(null);
        setMesaName('Minha mesa');
      }
    } catch {
      setSnackbar({ open: true, message: 'Erro ao excluir mesa', severity: 'error' });
    }
  };

  const newMesa = () => {
    setElements([]);
    setSelectedId(null);
    setMesaId(null);
    setMesaName('Minha mesa');
    setMesasDialogOpen(false);
  };

  const [acervoOpen, setAcervoOpen] = useState(false);
  const [acervoView, setAcervoView] = useState<'list' | 'grid'>('grid');
  const [preview, setPreview] = useState<{ src: string; name: string; acervoItemId?: string } | null>(null);
  const longPressRef = useRef<NodeJS.Timeout | null>(null);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [userEvents, setUserEvents] = useState<Event[]>([]);
  const [linkingEvent, setLinkingEvent] = useState(false);
  const [removingBg, setRemovingBg] = useState(false);
  const handleRemoveBg = async () => {
    const el = elements.find((e) => e.id === selectedId);
    if (!el) return;
    setRemovingBg(true);
    setSnackbar({ open: true, message: 'Removendo fundo, aguarde...', severity: 'success' });
    try {
      const cutout = await removeBackground(el.src);
      setElements((prev) => prev.map((e) => (e.id === el.id ? { ...e, src: cutout } : e)));
      setSnackbar({ open: true, message: 'Fundo removido', severity: 'success' });
      tap(ImpactStyle.Heavy);
    } catch (e: any) {
      setSnackbar({ open: true, message: e?.message || 'Não consegui remover o fundo desta imagem', severity: 'error' });
    } finally {
      setRemovingBg(false);
    }
  };

  const [size, setSize] = useState({ width: window.innerWidth, height: Math.round(window.innerHeight * 0.62) });
  useEffect(() => {
    const onResize = () => setSize({ width: window.innerWidth, height: Math.round(window.innerHeight * 0.62) });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const addImageFromFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const src = String(reader.result);
      const probe = new window.Image();
      probe.onload = () => {
        const maxW = size.width * 0.5;
        const scale = probe.width > maxW ? maxW / probe.width : 1;
        const width = probe.width * scale;
        const height = probe.height * scale;
        const id = `el_${idCounter.current++}_${Math.random().toString(36).slice(2, 7)}`;
        setElements((prev) => [
          ...prev,
          { id, src, x: (size.width - width) / 2, y: (size.height - height) / 2, width, height, rotation: 0 },
        ]);
        setSelectedId(id);
        tap(ImpactStyle.Medium);
      };
      probe.src = src;
    };
    reader.readAsDataURL(file);
  };

  const longPressFiredRef = useRef(false);

  const startLongPress = (src: string, name: string, acervoItemId?: string) => {
    cancelLongPress();
    longPressRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      tap(ImpactStyle.Medium);
      setPreview({ src, name, acervoItemId });
    }, 450);
  };

  const cancelLongPress = () => {
    if (longPressRef.current) {
      clearTimeout(longPressRef.current);
      longPressRef.current = null;
    }
  };

  const addImageFromUrl = (src: string, label: string, acervoItemId?: string) => {
    const probe = new window.Image();
    probe.onload = () => {
      const maxW = size.width * 0.5;
      const scale = probe.width > maxW ? maxW / probe.width : 1;
      const width = probe.width * scale;
      const height = probe.height * scale;
      const id = `el_${idCounter.current++}_${Math.random().toString(36).slice(2, 7)}`;
      setElements((prev) => [
        ...prev,
        { id, src, x: (size.width - width) / 2, y: (size.height - height) / 2, width, height, rotation: 0, ...(acervoItemId ? { acervoItemId } : {}) },
      ]);
      setSelectedId(id);
      tap(ImpactStyle.Medium);
    };
    probe.onerror = () => {
      setSnackbar({ open: true, message: `Não consegui carregar a imagem de ${label}`, severity: 'error' });
    };
    probe.src = src;
  };

  const addedFromRouteRef = useRef(false);
  useEffect(() => {
    const incoming = (location.state as any)?.addItem;
    if (!incoming?.photoUrl || addedFromRouteRef.current) return;
    addedFromRouteRef.current = true;
    addImageFromUrl(incoming.photoUrl, incoming.name || 'item do acervo', incoming.id);
    navigate('.', { replace: true, state: {} });
  }, [location.state]);

  const openedSavedOnMountRef = useRef(false);
  useEffect(() => {
    if (openedSavedOnMountRef.current || !user?.uid) return;
    if ((location.state as any)?.addItem) { openedSavedOnMountRef.current = true; return; }
    openedSavedOnMountRef.current = true;
    (async () => {
      try {
        const mesas = await getUserMesas(user.uid);
        if (mesas.length > 0) {
          setSavedMesas(mesas);
          setMesasDialogOpen(true);
        }
      } catch { void 0; }
    })();
  }, [user?.uid]);

  const removeSelected = () => {
    if (!selectedId) return;
    setElements((prev) => prev.filter((el) => el.id !== selectedId));
    setSelectedId(null);
    tap(ImpactStyle.Medium);
  };

  const rotateSelected = (delta: number) => {
    if (!selectedId) return;
    setElements((prev) => prev.map((el) => {
      if (el.id !== selectedId) return el;
      const next = ((el.rotation + delta) % 360 + 360) % 360;
      return { ...el, rotation: next };
    }));
    tap(ImpactStyle.Light);
  };

  const straightenSelected = () => {
    if (!selectedId) return;
    setElements((prev) => prev.map((el) => el.id === selectedId ? { ...el, rotation: 0 } : el));
    tap(ImpactStyle.Medium);
  };

  const bringToFront = () => {
    if (!selectedId) return;
    setElements((prev) => {
      const el = prev.find((e) => e.id === selectedId);
      if (!el) return prev;
      return [...prev.filter((e) => e.id !== selectedId), el];
    });
    tap();
  };

  const sendToBack = () => {
    if (!selectedId) return;
    setElements((prev) => {
      const el = prev.find((e) => e.id === selectedId);
      if (!el) return prev;
      return [el, ...prev.filter((e) => e.id !== selectedId)];
    });
    tap();
  };

  const openEventDialog = async () => {
    if (!user?.uid) return;
    setEventDialogOpen(true);
    try {
      setUserEvents(await getUserEvents(user.uid));
    } catch {
      setUserEvents([]);
    }
  };

  const linkMesaToEvent = async (ev: Event) => {
    const stage = stageRef.current;
    if (!user?.uid || !stage) return;
    setLinkingEvent(true);
    setSelectedId(null);
    try {
      await new Promise((r) => setTimeout(r, 60));
      const saved = await saveMesa(user.uid, mesaId, mesaName, elements, size);
      setMesaId(saved.id);
      setElements(saved.elements);
      let previewUrl = '';
      try {
        previewUrl = await saveMesaPreview(user.uid, saved.id, ev.id, stage.toDataURL({ pixelRatio: 1 }));
      } catch {
        setSnackbar({ open: true, message: 'Mesa salva, mas não consegui gerar a imagem dela para o evento.', severity: 'error' });
      }
      if (!previewUrl) { setEventDialogOpen(false); return; }

      const countByItem: Record<string, number> = {};
      saved.elements.forEach((el) => {
        if (el.acervoItemId) countByItem[el.acervoItemId] = (countByItem[el.acervoItemId] || 0) + 1;
      });
      const mesaItemIds = Object.keys(countByItem);
      const mergedItems = Array.from(new Set([...(ev.inventoryItems || []), ...mesaItemIds]));
      const mergedQty: Record<string, number> = { ...(ev.inventoryQuantities || {}) };
      mesaItemIds.forEach((id) => {
        mergedQty[id] = Math.max(mergedQty[id] || 0, countByItem[id]);
      });

      await updateEvent(ev.id, {
        mesa: { id: saved.id, name: mesaName.trim() || 'Minha mesa', previewUrl },
        inventoryItems: mergedItems,
        inventoryQuantities: mergedQty,
      });
      setEventDialogOpen(false);
      const addedMsg = mesaItemIds.length > 0 ? ` (${mesaItemIds.length} item(ns) do acervo incluidos)` : '';
      setSnackbar({ open: true, message: `Mesa adicionada ao evento "${ev.title}"${addedMsg}`, severity: 'success' });
      tap(ImpactStyle.Heavy);
    } catch (e: any) {
      setSnackbar({ open: true, message: e?.message || 'Erro ao adicionar a mesa ao evento', severity: 'error' });
    } finally {
      setLinkingEvent(false);
    }
  };

  const exportPng = async () => {
    const stage = stageRef.current;
    if (!stage) return;
    setSelectedId(null);
    await new Promise((r) => setTimeout(r, 60));
    let uri = '';
    try {
      uri = stage.toDataURL({ pixelRatio: 2 });
    } catch {
      setSnackbar({ open: true, message: 'Não consegui exportar: uma das imagens bloqueia a exportação.', severity: 'error' });
      return;
    }
    if (Capacitor.isNativePlatform()) {
      try {
        const fileName = `mesa-hostly-${Date.now()}.png`;
        const written = await Filesystem.writeFile({ path: fileName, data: uri.split(',')[1], directory: Directory.Cache });
        await Share.share({ title: 'Minha mesa', files: [written.uri] });
        setSnackbar({ open: true, message: 'Mesa exportada', severity: 'success' });
        tap(ImpactStyle.Heavy);
      } catch {
        setSnackbar({ open: true, message: 'Não consegui exportar a imagem', severity: 'error' });
      }
    } else {
      const link = document.createElement('a');
      link.download = 'mesa-hostly.png';
      link.href = uri;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setSnackbar({ open: true, message: 'Mesa exportada como imagem', severity: 'success' });
      tap(ImpactStyle.Heavy);
    }
  };

  const updateElement = (next: MesaElement) => {
    setElements((prev) => prev.map((el) => (el.id === next.id ? next : el)));
  };

  if (!isPro) {
    return (
      <>
        <TopBar title="Mesa virtual" icon={<HomeIcon sx={{ fontSize: '0.875rem' }} />} showBackButton onBackClick={() => navigate('/inventory')} />
        <Box sx={{ pt: '80px', px: 3, minHeight: '100vh', backgroundColor: theme.colors.background }}>
          <FeatureLockedOverlay
            feature="Mesa virtual"
            requiredPlan="pro"
            onUpgrade={() => navigate('/pricing')}
            variant="card"
          />
        </Box>
      </>
    );
  }

  return (
    <>
      <TopBar title="Mesa virtual" icon={<HomeIcon sx={{ fontSize: '0.875rem' }} />} showBackButton onBackClick={() => navigate('/inventory')} />

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) addImageFromFile(file);
          e.target.value = '';
        }}
      />

      <Box sx={{ pt: '64px', pb: 2, minHeight: '100vh', backgroundColor: theme.colors.background }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', px: 2, py: 1.5 }}>
          <Button
            variant="contained"
            startIcon={<AddPhotoIcon />}
            onClick={() => setAcervoOpen(true)}
            sx={{ backgroundColor: theme.colors.primary, textTransform: 'none' }}
          >
            Do meu acervo
          </Button>
          <Button
            variant="text"
            onClick={() => fileInputRef.current?.click()}
            sx={{ color: theme.colors.text.secondary, textTransform: 'none' }}
          >
            Outra imagem
          </Button>
          <Button
            variant="text"
            startIcon={<CollectionsIcon />}
            onClick={openMesasDialog}
            sx={{ color: theme.colors.text.primary, textTransform: 'none' }}
          >
            Minhas mesas
          </Button>
          <Box sx={{ flex: 1 }} />
          <IconButton onClick={exportPng} disabled={elements.length === 0} sx={{ color: theme.colors.primary }} aria-label="Exportar como imagem">
            <DownloadIcon />
          </IconButton>
          <Button
            variant="text"
            onClick={openEventDialog}
            disabled={elements.length === 0}
            sx={{ color: theme.colors.text.primary, textTransform: 'none' }}
          >
            Adicionar a um evento
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={() => setSaveDialogOpen(true)}
            disabled={elements.length === 0}
            sx={{ backgroundColor: theme.colors.secondary, textTransform: 'none' }}
          >
            Salvar
          </Button>
        </Box>

        <Box sx={{ position: 'relative', borderTop: `1px solid ${theme.colors.border}`, borderBottom: `1px solid ${theme.colors.border}`, overflow: 'hidden' }}>
          <Stage
            ref={stageRef}
            width={size.width}
            height={size.height}
            onMouseDown={(e) => { if (e.target === e.target.getStage() || e.target.name() === 'bg') setSelectedId(null); }}
            onTouchStart={(e) => { if (e.target === e.target.getStage() || e.target.name() === 'bg') setSelectedId(null); }}
          >
            <Layer>
              <Rect name="bg" x={0} y={0} width={size.width} height={size.height} fill="#EFE7DD" />
            </Layer>
            <Layer>
              {elements.map((el) => (
                <MesaImage
                  key={el.id}
                  element={el}
                  isSelected={el.id === selectedId}
                  onSelect={() => setSelectedId(el.id)}
                  onChange={updateElement}
                />
              ))}
            </Layer>
          </Stage>

          {selectedId && (
            <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 8, display: 'flex', justifyContent: 'center', px: 1, pointerEvents: 'none' }}>
              <Box
                sx={{
                  pointerEvents: 'auto',
                  display: 'flex',
                  gap: 0.5,
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                  px: 1.5,
                  py: 1,
                  maxWidth: '96%',
                  borderRadius: theme.radii.medium,
                  backgroundColor: 'rgba(255, 255, 255, 0.94)',
                  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.18)',
                }}
              >
                <Button
                  variant="contained"
                  size="small"
                  startIcon={removingBg ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <MagicIcon />}
                  onClick={handleRemoveBg}
                  disabled={removingBg}
                  sx={{ backgroundColor: theme.colors.primary, textTransform: 'none' }}
                >
                  Remover fundo
                </Button>
                <IconButton size="small" onClick={() => rotateSelected(-15)} sx={{ color: theme.colors.text.primary }} aria-label="Girar para a esquerda"><RotateLeftIcon /></IconButton>
                <IconButton size="small" onClick={() => rotateSelected(15)} sx={{ color: theme.colors.text.primary }} aria-label="Girar para a direita"><RotateRightIcon /></IconButton>
                <IconButton size="small" onClick={straightenSelected} sx={{ color: theme.colors.text.primary }} aria-label="Desentortar"><StraightenIcon /></IconButton>
                <IconButton size="small" onClick={bringToFront} sx={{ color: theme.colors.text.primary }} aria-label="Trazer para frente"><FrontIcon /></IconButton>
                <IconButton size="small" onClick={sendToBack} sx={{ color: theme.colors.text.primary }} aria-label="Enviar para trás"><BackIcon /></IconButton>
                <IconButton size="small" onClick={removeSelected} sx={{ color: '#d32f2f' }} aria-label="Remover"><DeleteIcon /></IconButton>
              </Box>
            </Box>
          )}
        </Box>

        {elements.length === 0 && (
          <Box sx={{ textAlign: 'center', color: theme.colors.text.secondary, mt: 3, px: 3 }}>
            Adicione imagens e monte sua mesa. Arraste, redimensione e use os botões de girar (ou Desentortar para alinhar).
          </Box>
        )}
      </Box>

      <Dialog open={eventDialogOpen} onClose={() => !linkingEvent && setEventDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Adicionar a um evento</DialogTitle>
        <DialogContent dividers>
          {userEvents.length === 0 ? (
            <Typography variant="body2" sx={{ color: theme.colors.text.secondary, py: 2 }}>
              Você ainda não tem eventos.
            </Typography>
          ) : (
            <List>
              {userEvents.map((ev) => (
                <ListItemButton
                  key={ev.id}
                  disabled={linkingEvent}
                  onClick={() => linkMesaToEvent(ev)}
                  sx={{ borderRadius: theme.radii.medium, mb: 0.5, border: `1px solid ${theme.colors.border}` }}
                >
                  <ListItemText
                    primary={ev.title}
                    secondary={ev.date ? new Date(ev.date).toLocaleDateString('pt-BR') : ''}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
          {linkingEvent && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}><CircularProgress size={22} /></Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEventDialogOpen(false)} disabled={linkingEvent} sx={{ color: theme.colors.text.secondary }}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>{preview?.name}</DialogTitle>
        <DialogContent>
          {preview && (
            <Box
              component="img"
              src={preview.src}
              alt={preview.name}
              sx={{ width: '100%', maxHeight: '70vh', objectFit: 'contain', display: 'block' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreview(null)} sx={{ color: theme.colors.text.secondary }}>Fechar</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (preview) addImageFromUrl(preview.src, preview.name, preview.acervoItemId);
              setPreview(null);
              setAcervoOpen(false);
            }}
            sx={{ backgroundColor: theme.colors.primary }}
          >
            Adicionar à mesa
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={acervoOpen} onClose={() => setAcervoOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Do meu acervo
          <ToggleButtonGroup
            value={acervoView}
            exclusive
            size="small"
            onChange={(_, v) => v && setAcervoView(v)}
          >
            <ToggleButton value="grid" aria-label="Grade"><GridViewIcon fontSize="small" /></ToggleButton>
            <ToggleButton value="list" aria-label="Lista"><ViewListIcon fontSize="small" /></ToggleButton>
          </ToggleButtonGroup>
        </DialogTitle>
        <DialogContent dividers sx={scrollableSx(theme)}>
          <Typography variant="caption" sx={{ color: theme.colors.text.secondary, display: 'block', mb: 1 }}>
            Toque para adicionar. Segure para ver a foto ampliada.
          </Typography>
          {acervoItems.filter((i) => !!i.photoUrl).length === 0 ? (
            <Typography variant="body2" sx={{ color: theme.colors.text.secondary, py: 2 }}>
              Nenhum item do acervo com foto ainda.
            </Typography>
          ) : acervoView === 'grid' ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
              {acervoItems.filter((i) => !!i.photoUrl).map((it) => (
                <Box
                  key={it.id}
                  onClick={() => {
                    if (longPressFiredRef.current) { longPressFiredRef.current = false; return; }
                    addImageFromUrl(it.photoUrl as string, it.itemName, it.id);
                    setAcervoOpen(false);
                  }}
                  onContextMenu={(e) => e.preventDefault()}
                  onTouchStart={() => startLongPress(it.photoUrl as string, it.itemName, it.id)}
                  onTouchEnd={cancelLongPress}
                  onTouchMove={cancelLongPress}
                  onMouseDown={() => startLongPress(it.photoUrl as string, it.itemName, it.id)}
                  onMouseUp={cancelLongPress}
                  onMouseLeave={cancelLongPress}
                  sx={{ cursor: 'pointer', borderRadius: theme.radii.medium, border: `1px solid ${theme.colors.border}`, overflow: 'hidden' }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      aspectRatio: '1 / 1',
                      backgroundColor: '#EFE7DE',
                      backgroundImage: `url(${it.photoUrl})`,
                      backgroundSize: 'contain',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                    }}
                  />
                  <Typography variant="caption" sx={{ display: 'block', p: 0.75, color: theme.colors.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {it.itemName}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <List>
              {acervoItems.filter((i) => !!i.photoUrl).map((it) => (
                <ListItemButton
                  key={it.id}
                  onClick={() => {
                    if (longPressFiredRef.current) { longPressFiredRef.current = false; return; }
                    addImageFromUrl(it.photoUrl as string, it.itemName, it.id);
                    setAcervoOpen(false);
                  }}
                  onContextMenu={(e) => e.preventDefault()}
                  onTouchStart={() => startLongPress(it.photoUrl as string, it.itemName, it.id)}
                  onTouchEnd={cancelLongPress}
                  onTouchMove={cancelLongPress}
                  onMouseDown={() => startLongPress(it.photoUrl as string, it.itemName, it.id)}
                  onMouseUp={cancelLongPress}
                  onMouseLeave={cancelLongPress}
                  sx={{ borderRadius: theme.radii.medium, mb: 0.5, border: `1px solid ${theme.colors.border}` }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      mr: 1.5,
                      flexShrink: 0,
                      borderRadius: theme.radii.small,
                      backgroundImage: `url(${it.photoUrl})`,
                      backgroundSize: 'contain',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                    }}
                  />
                  <ListItemText primary={it.itemName} secondary={it.category || ''} />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAcervoOpen(false)} sx={{ color: theme.colors.text.secondary }}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={saveDialogOpen} onClose={() => !saving && setSaveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Salvar mesa</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Nome da mesa"
            value={mesaName}
            onChange={(e) => setMesaName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setSaveDialogOpen(false)} disabled={saving} sx={{ color: theme.colors.text.secondary }}>Cancelar</Button>
          {mesaId && (
            <Button onClick={() => persist(true)} disabled={saving} sx={{ color: theme.colors.primary }}>
              Salvar como nova
            </Button>
          )}
          <Button variant="contained" onClick={() => persist(false)} disabled={saving} sx={{ backgroundColor: theme.colors.primary }}>
            {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : (mesaId ? 'Atualizar' : 'Salvar')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={mesasDialogOpen} onClose={() => setMesasDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Minhas mesas</DialogTitle>
        <DialogContent dividers>
          <Button variant="outlined" fullWidth onClick={newMesa} sx={{ mb: 2, textTransform: 'none', color: theme.colors.primary, borderColor: theme.colors.primary }}>
            Nova mesa em branco
          </Button>
          {loadingMesas ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
          ) : savedMesas.length === 0 ? (
            <Typography variant="body2" sx={{ color: theme.colors.text.secondary, textAlign: 'center', py: 2 }}>
              Nenhuma mesa salva ainda.
            </Typography>
          ) : (
            <List>
              {savedMesas.map((m) => (
                <ListItemButton key={m.id} onClick={() => openMesa(m.id)} sx={{ borderRadius: theme.radii.medium, mb: 0.5, border: `1px solid ${theme.colors.border}`, alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      flexShrink: 0,
                      borderRadius: theme.radii.small,
                      border: `1px solid ${theme.colors.border}`,
                      backgroundColor: '#EFE7DE',
                      backgroundImage: m.thumbUrl ? `url(${m.thumbUrl})` : 'none',
                      backgroundSize: 'contain',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {!m.thumbUrl && <CollectionsIcon sx={{ color: theme.colors.text.secondary, fontSize: 20 }} />}
                  </Box>
                  <ListItemText
                    primary={m.name}
                    secondary={`${m.elements.length} peça(s)`}
                    primaryTypographyProps={{ fontWeight: 600, color: theme.colors.text.primary }}
                  />
                  <IconButton edge="end" onClick={(e) => { e.stopPropagation(); removeMesa(m); }} sx={{ color: '#d32f2f' }} aria-label="Excluir mesa">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMesasDialogOpen(false)} sx={{ color: theme.colors.text.secondary }}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
